<script lang="ts">
	/**
	 * `/Checkout/Pay` — where the customer pays, for a provider that hosts no page of its own
	 * (Design §6.2).
	 *
	 * Every other checkout leaves the store at this point: `createCheckoutSession` hands back a URL
	 * at the provider and `/Checkout/Start` sends the browser there. Braintree has no such page, so
	 * the URL it hands back points here instead — which is why this page needed no new branch
	 * anywhere in the flow. It is reached the same way, from the same redirect, and it hands off to
	 * the same `/Checkout/Return` afterwards.
	 *
	 * It is deliberately **not** in `AbstractID.publicRoutes`. Every request it makes is
	 * `requireAuth` and about a session the arriving visitor must already own, so an arrival with no
	 * token is a stale link and the inherited redirect is the correct guard.
	 *
	 * Two things it does not do, both on purpose. It **grants nothing** — like `/Checkout/Return`,
	 * it renders what the server says and never decides that a purchase happened. And it **states
	 * what is being bought** rather than presenting a bare card form: this is the last screen before
	 * money moves, and the one thing worse than asking for a card is asking for one beside nothing
	 * that says what it is for.
	 *
	 * **It asks for the billing address**, because nothing else in a Braintree checkout does. A
	 * hosted page collects one and `syncFromProvider` mirrors it back; Braintree's Drop-in collects a
	 * card and nothing around it, so a customer who bought here had a country and no address — on
	 * every invoice the store issued them, and on the Billing page they were never sent to. It is
	 * saved to the customer **before** the payment is confirmed, because confirming issues the
	 * invoice and the invoice reads the customer as it stands.
	 *
	 * The country is shown, not asked. The tax on this purchase was worked out from it when the
	 * session was created and is frozen with it, so a country changed here would put one address on
	 * the invoice and another's tax on the charge. A customer in the wrong country goes back a step.
	 */

	import { onMount } from 'svelte';
	import { push } from '../../svelte-spa-router';
	import { Input } from 'flowbite-svelte';
	import {
		BraintreeDropIn,
		EmptyState,
		FormField,
		PageHeader,
		RoutedButton,
		PaymentSkeleton
	} from '../../components';
	import {
		conditionOf,
		confirmCheckoutSession,
		getAccountSummary,
		getCheckoutClientToken,
		getCheckoutSession,
		updateBilling
	} from '../../AppNetwork';
	import { countryName } from '../../Countries';
	import type { CheckoutSessionState } from '../../AppTypes';
	import { flush, track } from '../../stores/analytics';

	let phase: 'loading' | 'ready' | 'failed' = 'loading';
	let failureMessage: string = '';
	/**
	 * Set when the refusal is one the customer can act on themselves — a session that is not ours to
	 * take payment for, or one that has already been paid. Both want a link out rather than a retry.
	 */
	let failureCondition: string = '';

	let sessionUUID: string = '';
	let session: CheckoutSessionState | null = null;
	let clientToken: string | null = null;
	let submitting: boolean = false;
	let submitError: string = '';

	/** The billing address, prefilled from the customer on file and saved before the charge. */
	let billingName: string = '';
	let address = { country: '', state: '', postalCode: '', line1: '', line2: '', city: '' };

	$: addressComplete =
		address.country !== '' &&
		address.line1.trim() !== '' &&
		address.city.trim() !== '' &&
		address.postalCode.trim() !== '';

	onMount(() => {
		sessionUUID = new URLSearchParams(window.location.search).get('session') ?? '';
		void load();
	});

	async function load(): Promise<void> {
		phase = 'loading';
		failureMessage = '';
		failureCondition = '';

		if (sessionUUID === '') {
			failureMessage = 'This payment link is missing its checkout, so there is nothing to pay for.';
			phase = 'failed';
			return;
		}

		try {
			/*
			 * The session first, and its failure is **not** swallowed — unlike the decorative reads
			 * elsewhere in the flow. Here it is the only thing that says what is being bought, and a
			 * card form over an unnamed amount is not a screen to show anybody.
			 */
			session = await getCheckoutSession(sessionUUID);

			// Already finished — the customer reloaded an old tab, or paid in another one. Sending
			// them to the return page shows them the purchase instead of asking for a second card.
			if (session.status !== 'pending') {
				push(`/Checkout/Return?session=${encodeURIComponent(sessionUUID)}`);
				return;
			}

			clientToken = (await getCheckoutClientToken(sessionUUID)).token;
			await prefill();
			phase = 'ready';
			track('checkout_step', {
				productSlug: session.productSlug,
				properties: { step: 'payment', action: 'opened' }
			});
		} catch (error) {
			failureMessage = (error as Error).message;
			failureCondition = conditionOf(error) ?? '';
			phase = 'failed';
		}
	}

	/**
	 * What the customer already told us, so a returning customer confirms rather than retypes.
	 * Swallowed: an empty form costs some typing, never the purchase.
	 */
	async function prefill(): Promise<void> {
		try {
			const { customer } = await getAccountSummary();
			billingName = customer.billingName ?? '';
			address = {
				country: (customer.address.country ?? '').toUpperCase(),
				state: customer.address.state ?? '',
				postalCode: customer.address.postalCode ?? '',
				line1: customer.address.line1 ?? '',
				line2: customer.address.line2 ?? '',
				city: customer.address.city ?? ''
			};
		} catch {
			// The country is still required below; with none on file the form says so.
		}
	}

	function blankToNull(value: string): string | null {
		return value.trim() === '' ? null : value.trim();
	}

	/**
	 * Hands the token to the server, which is where the sale or the subscription is created.
	 *
	 * The reply is the session in whatever state paying left it, which is **not** always paid: a
	 * delayed-settlement method completes `unpaid` and settles days later. So this routes to the
	 * return page and lets it render the outcome, rather than claiming one here.
	 */
	async function pay(event: CustomEvent<{ paymentToken: string }>): Promise<void> {
		submitting = true;
		submitError = '';
		try {
			// The address first: confirming issues the invoice, and the invoice reads the customer.
			await updateBilling({
				...(billingName.trim() === '' ? {} : { billingName: billingName.trim() }),
				address: {
					country: address.country,
					state: blankToNull(address.state),
					postalCode: blankToNull(address.postalCode),
					line1: blankToNull(address.line1),
					line2: blankToNull(address.line2),
					city: blankToNull(address.city)
				}
			});
			await confirmCheckoutSession(sessionUUID, event.detail.paymentToken);
			// The last event before the page goes; a `fetch` issued during a navigation is cancelled
			// by it, so the queue is flushed rather than left to its timer.
			void flush(true);
			push(`/Checkout/Return?session=${encodeURIComponent(sessionUUID)}`);
		} catch (error) {
			// The form is still on screen and still correctable — a declined card is retried with
			// another one — so this renders beside it rather than replacing the page.
			submitError = (error as Error).message;
			submitting = false;
		}
	}
</script>

<div class="mx-auto w-full max-w-lg space-y-4 p-4">
	<PageHeader title="Payment" description="Enter your card details to complete your purchase." />

	{#if phase === 'loading'}
		<PaymentSkeleton />
	{:else if phase === 'failed'}
		<EmptyState
			variant="error"
			title={failureCondition === 'checkout.notHostedHere'
				? 'This purchase is paid for somewhere else'
				: 'This payment could not be started'}
			description={failureMessage}
		/>
		<RoutedButton href="/">Back to the store</RoutedButton>
	{:else}
		<div class="widget space-y-2">
			<p class="card-label">You are buying</p>
			<p class="text-base font-medium">{session?.productName ?? ''}</p>
		</div>

		<div class="widget space-y-3">
			<p class="card-label">Billing address</p>
			{#if address.country === ''}
				<p class="text-sm text-red-700 dark:text-red-400">
					We do not know which country you are buying from. Go back to the product and choose it there, so the
					price can be worked out for it.
				</p>
			{:else}
				<FormField id="pay-name" label="Name on the invoice">
					<Input id="pay-name" autocomplete="name" bind:value={billingName} disabled={submitting} />
				</FormField>
				<FormField id="pay-line1" label="Address" required>
					<Input id="pay-line1" autocomplete="address-line1" bind:value={address.line1} disabled={submitting} />
				</FormField>
				<FormField id="pay-line2" label="Address line 2">
					<Input id="pay-line2" autocomplete="address-line2" bind:value={address.line2} disabled={submitting} />
				</FormField>
				<div class="grid gap-3 sm:grid-cols-2">
					<FormField id="pay-city" label="City" required>
						<Input id="pay-city" autocomplete="address-level2" bind:value={address.city} disabled={submitting} />
					</FormField>
					<FormField id="pay-postal" label="Postcode" required>
						<Input id="pay-postal" autocomplete="postal-code" bind:value={address.postalCode} disabled={submitting} />
					</FormField>
				</div>
				<FormField id="pay-state" label="State, province or region">
					<Input id="pay-state" autocomplete="address-level1" bind:value={address.state} disabled={submitting} />
				</FormField>
				<p class="text-sm text-default">
					Country: <strong>{countryName(address.country)}</strong> — the price was worked out for it.
				</p>
			{/if}
		</div>

		<div class="widget">
			<BraintreeDropIn
				{clientToken}
				submitLabel="Pay now"
				busy={submitting}
				disabled={!addressComplete}
				error={submitError}
				on:token={pay}
			/>
			{#if !addressComplete && address.country !== ''}
				<p class="text-xs text-default mt-2">Fill in your billing address above to pay.</p>
			{/if}
		</div>
	{/if}
</div>
